import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiAward, FiStar, FiTarget } from 'react-icons/fi';

const AwardsSection = styled.section`
  padding: 100px 150px;
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const AwardsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const AwardsTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const AwardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
`;

const AwardIconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  box-shadow: 0 10px 25px rgba(2, 12, 27, 0.2);
  border: 1px solid var(--bg-highlight);
  transition: all 0.3s ease;
  
  svg {
    font-size: 24px;
    color: var(--accent-primary);
  }
`;

const AwardCard = styled(motion.div)`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${AwardIconWrapper} {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(100, 255, 218, 0.3);
      border-color: var(--accent-primary);
    }
  }
`;

const AwardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const AwardDate = styled.div`
  color: var(--accent-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  margin-bottom: 15px;
`;

const AwardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  flex-grow: 1;
`;

const AwardFooter = styled.div`
  margin-top: 20px;
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--accent-highlight);
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
      duration: 0.5
    }
  }
};

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const Awards = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [gridRef, gridInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const awardsList = [
    {
      id: 1,
      title: "Star of the Month",
      organization: "Tata Consultancy Services",
      date: "2024",
      description: "Awarded for exceptional performance in delivering security solutions and identifying critical vulnerabilities.",
      icon: <FiStar />
    },
    {
      id: 2,
      title: "Special Initiative Award",
      organization: "Tata Consultancy Services",
      date: "2024",
      description: "Honored for innovative contributions to security automation and threat detection processes.",
      icon: <FiAward />
    },
    {
      id: 3,
      title: "Star of the Month",
      organization: "Tata Consultancy Services",
      date: "2023",
      description: "Recognized for outstanding performance in security assessments and vulnerability remediation guidance.",
      icon: <FiStar />
    },
    {
      id: 4,
      title: "Special Initiative Award",
      organization: "Tata Consultancy Services",
      date: "2021",
      description: "Honored for pioneering efforts in implementing secure coding practices and security training initiatives.",
      icon: <FiAward />
    },
    {
      id: 5,
      title: "TCS HACKQUEST Season 5",
      organization: "Tata Consultancy Services",
      date: "2021",
      description: "Competed and excelled in a national cybersecurity challenge, demonstrating advanced penetration testing skills.",
      icon: <FiTarget />
    },
    {
      id: 6,
      title: "Programming Level 5 Hackathon",
      organization: "CMR Institute of Technology",
      date: "2020",
      description: "Won top honors in an advanced programming competition, showcasing exceptional coding and problem-solving abilities.",
      icon: <FiAward />
    }
  ];
  
  return (
    <AwardsSection id="awards">
      <AwardsContainer>
        <AwardsTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Honors & Awards
        </AwardsTitle>
        
        <AwardsGrid
          ref={gridRef}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={gridInView ? "visible" : "hidden"}
        >
          {awardsList.map((award) => (
            <AwardCard
              key={award.id}
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              <AwardIconWrapper>
                {award.icon}
              </AwardIconWrapper>
              <AwardTitle>{award.title}</AwardTitle>
              <AwardDate>{award.date} • {award.organization}</AwardDate>
              <AwardDescription>{award.description}</AwardDescription>
            </AwardCard>
          ))}
        </AwardsGrid>
      </AwardsContainer>
    </AwardsSection>
  );
};

export default Awards; 